import {Card, DatePicker, Select} from 'antd';
import {
  Currencies,
  Interval,
  useAccountQuery,
  useOnUpdateAnalyticsSubscription,
  useSucceededDateRangeKpiQuery
} from 'api/generated';
import {Stack} from 'components';
import moment, {Moment} from 'moment';
import React, {useState} from 'react';
import {ReportChart} from 'routes/Dashboard/ReportChart';
import {useDebouncedCallback} from 'use-debounce';
import {FilterByValue} from './Dashboard';

type Props = {
  accountId?: string;
  selectedCurrency: Currencies;
  setCurrency: (currency: Currencies) => void;
  currencies?: Currencies[];
} & FilterByValue;

type ChartRow = {
  timestamp: number;
  first?: number;
  second?: number;
};
type ValueType = 'cuSucceededAmount' | 'cuSucceededCount';
const label = {
  cuSucceededAmount: 'Gross volume',
  cuSucceededCount: 'Successful payments'
};

const chartHeight = 280;

export const TodayChart: React.FC<Props> = ({
  accountId,
  filterBy,
  filterValue,
  selectedCurrency,
  setCurrency
}) => {
  const now = moment();
  const yesterday = now.clone().subtract(1, 'day');

  const [type, setType] = useState<ValueType>('cuSucceededAmount');
  const [date, setDate] = useState(yesterday);
  const totalType = type === 'cuSucceededAmount' ? 'succeededAmount' : 'succeededCount';
  const {data} = useAccountQuery({variables: {id: accountId!}, skip: !accountId});
  const account = data?.account;

  const {
    data: first,
    loading: firstLoading,
    refetch
  } = useSucceededDateRangeKpiQuery({
    variables: {
      accountId,
      currency: selectedCurrency,
      [filterBy as string]: filterValue,
      start: now.startOf('day').unix(),
      end: now.endOf('day').unix(),
      interval: Interval.HOUR
    },
    fetchPolicy: 'network-only'
  });
  const {data: second, loading: secondLoading} = useSucceededDateRangeKpiQuery({
    variables: {
      accountId,
      [filterBy as string]: filterValue,
      currency: selectedCurrency,
      start: date.clone().startOf('day').unix(),
      end: date.clone().endOf('day').unix(),
      interval: Interval.HOUR
    }
  });
  const update = useDebouncedCallback(refetch, 5000);
  useOnUpdateAnalyticsSubscription({
    onSubscriptionData: () => update()
  });
  const loading = firstLoading || secondLoading;
  const chartData: ChartRow[] = [];
  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: first?.chargesDateRangeKPI.currency ?? 'EUR'
  });
  const total = {
    first: first?.chargesDateRangeKPI.total[totalType] ?? 0,
    second: second?.chargesDateRangeKPI.total[totalType] ?? 0
  };
  first?.chargesDateRangeKPI.data.forEach((row) => {
    chartData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row[type] : undefined
    });
  });
  second?.chargesDateRangeKPI.data.forEach((row, i) => {
    if (chartData[i]) {
      chartData[i].second = row[type];
    }
  });
  const formatValue = (value: number) => {
    if (type === 'cuSucceededCount') return value.toLocaleString();
    return currency.format(value / 100);
  };
  return (
    <Card
      title="Today"
      size="small"
      style={{marginBottom: 20}}
      bodyStyle={{backgroundColor: '#f7fafc'}}
      headStyle={{marginBottom: 0}}
      extra={
        <Stack>
          <Select
            style={{width: 180}}
            size="small"
            value={type}
            onChange={(value: ValueType) => setType(value)}>
            <Select.Option value="cuSucceededAmount">{label.cuSucceededAmount}</Select.Option>
            <Select.Option value="cuSucceededCount">{label.cuSucceededCount}</Select.Option>
          </Select>
          <DatePicker
            allowClear={false}
            value={date}
            format="L"
            size="small"
            style={{width: 115}}
            showToday={false}
            onChange={(moment) => setDate(moment as Moment)}
            disabledDate={(current) => current!.isAfter(yesterday, 'day')}
          />
          {(!account || account.defaultCurrency !== Currencies.EUR) && (
            <Select
              style={{width: 70}}
              size="small"
              value={selectedCurrency}
              onChange={(value: Currencies) => setCurrency(value)}>
              {Object.values(Currencies).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          )}
        </Stack>
      }>
      <ReportChart
        loading={loading}
        height={chartHeight}
        data={chartData}
        dataLabel={label[type]}
        minTickGap={30}
        interval="preserveStart"
        timestampLabel={date.isSame(yesterday, 'day') ? 'Yesterday' : date.format('ll')}
        total={total}
        fullLegend
        showLine
        formatTimestamp={(value) => moment.unix(value).format('LT')}
        formatValue={formatValue}
      />
    </Card>
  );
};
